import React from 'react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './providers/AuthenticationProvider';
import { ProductProvider } from './providers/ProductProvider';
import { UserProvider } from './providers/UsersProvider';
import { UserSearchProvider } from './providers/UserSearchProvider';
import { ProposalProvider } from './providers/ProposalProvider';
import { DashboardProvider } from './providers/DashboardProvider';
import { CustomerProvider } from './providers/CustomerProvider';
import { DistributorProvider } from './providers/DistributorProvider';
import { DistributorSettingsProvider } from './providers/DistributorSettingsProvider';
import { ReportProvider } from './providers/ReportProvider';
import { SiteHealthProvider } from './providers/SiteHealthProvider';
import { DataInspectorProvider } from './providers/DataInspectorProvider';
import { DataMaintenanceProvider } from './providers/DataMaintenanceProvider';
import 'semantic-ui-css/semantic.min.css';
import 'react-semantic-toasts/styles/react-semantic-alert.css';
import { UtilityAccountProvider } from './providers/UtilityAccountProvider';
import { UtilityMeterProvider } from './providers/UtilityMeterProvider';
import { UnitConversionsProvider } from './providers/UnitConversionsProvider';
import { PointMappingProvider } from './providers/PointMappingProvider';
import { GenericPointProvider } from './providers/GenericPointProvider';
import { EquipmentProvider } from "./providers/EquipmentProvider";
import { WeatherStationProvider } from "./providers/WeatherStationProvider";
import { IssueOptionsProvider } from './providers/IssueOptionsProvider';
import { TaskOptionsProvider } from './providers/TaskOptionsProvider';
import { ComponentManagerProvider } from "./providers/ComponentManagerProvider";
import { ScheduleTreeProvider } from "./providers/ScheduleTreeProvider";
import { MappingTreeProvider } from "./providers/MappingTreeProvider";
import { createRoot } from 'react-dom/client';
import { init as initApm } from '@elastic/apm-rum';
import * as Sentry from "@sentry/react";

// setup elastic rum
if(process.env.REACT_APP_APM_SERVER_URL) {
    var apm = initApm({
        serviceName: process.env.REACT_APP_APM_SERVICE_NAME || "pathian-portal-ui-test",
        serverUrl: process.env.REACT_APP_APM_SERVER_URL,
        serviceVersion: process.env.REACT_APP_APM_SERVICE_VERSION,
        logLevel: 'debug',
        environment: process.env.REACT_APP_APM_ENVIRONMENT || "test"
    });
    if(process.env.REACT_APP_APM_CONSOLEERRORS == 'true') {
        const baseConsoleError = console.error;
        let consoleErrorNestCount = 0;
        console.error = (...args) => {
            if(consoleErrorNestCount >= 2) {
                baseConsoleError(...args);
                return;
            }
            try {
                consoleErrorNestCount++;
                baseConsoleError(...args);
                let error = args[0];
                if(typeof error !== 'string' && !(error instanceof Error)) {
                    if(typeof error === 'object' && error != null) {
                        error = JSON.stringify(error);
                    } else {
                        error = ''+error;
                    }
                }
                let message = undefined;
                if(args.length > 1) {
                    message = args.slice(1).join('\n\n');
                }
                apm.captureError(error, {
                    message
                });
            } finally {
                consoleErrorNestCount--;
            }
        };
    }
}
// setup sentry.io
if(process.env.REACT_APP_SENTRY_DSN) {
    const tracePropTargets = ['localhost'];
    if(process.env.REACT_APP_SENTRY_TRACEPROP_TARGET_API) {
        tracePropTargets.push(new RegExp(process.env.REACT_APP_SENTRY_TRACEPROP_TARGET_API));
    }
    if(process.env.REACT_APP_SENTRY_TRACEPROP_TARGET_DATAAPI) {
        tracePropTargets.push(new RegExp(process.env.REACT_APP_SENTRY_TRACEPROP_TARGET_DATAAPI));
    }
    Sentry.init({
        release: process.env.REACT_APP_SENTRY_RELEASE,
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.captureConsoleIntegration({
                levels: ['error']
            })
        ],
        // Tracing
        tracesSampleRate: 0.05, //  Capture 5% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: tracePropTargets,
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

const root = createRoot(document.getElementById('root'));

root.render(
    <BrowserRouter>
        <AuthProvider>
            <ReportProvider>
                <ProductProvider>
                    <DistributorProvider>
                        <UserProvider>
                            <CustomerProvider>
                                <UserSearchProvider>
                                    <ProposalProvider>
                                        <DistributorSettingsProvider>
                                            <DashboardProvider>
                                                <DataInspectorProvider>
                                                    <SiteHealthProvider>
                                                        <DataMaintenanceProvider>
                                                            <UtilityAccountProvider>
                                                                <UtilityMeterProvider>
                                                                    <GenericPointProvider>
                                                                        <PointMappingProvider>
                                                                            <EquipmentProvider>
                                                                                <UnitConversionsProvider>
                                                                                    <WeatherStationProvider>
                                                                                        <IssueOptionsProvider>
                                                                                            <TaskOptionsProvider>
                                                                                                <ComponentManagerProvider>
                                                                                                    <ScheduleTreeProvider>
                                                                                                        <MappingTreeProvider>
                                                                                                            <App />
                                                                                                        </MappingTreeProvider>
                                                                                                    </ScheduleTreeProvider>
                                                                                                </ComponentManagerProvider>
                                                                                            </TaskOptionsProvider>
                                                                                        </IssueOptionsProvider>
                                                                                    </WeatherStationProvider>
                                                                                </UnitConversionsProvider>
                                                                            </EquipmentProvider>
                                                                        </PointMappingProvider>
                                                                    </GenericPointProvider>
                                                                </UtilityMeterProvider>
                                                            </UtilityAccountProvider>
                                                        </DataMaintenanceProvider>
                                                    </SiteHealthProvider>
                                                </DataInspectorProvider>
                                            </DashboardProvider>
                                        </DistributorSettingsProvider>
                                    </ProposalProvider>
                                </UserSearchProvider>
                            </CustomerProvider>
                        </UserProvider>
                    </DistributorProvider>
                </ProductProvider>
            </ReportProvider>
        </AuthProvider>
    </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
